import CustomerYearEndBooking from '../../Accounting/Periodisering/CustomerYearEndBooking';
import { Link } from 'react-router-dom';
import React, { useCallback, useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import moment from 'moment';
import { toast } from 'sonner';
import {
    CheckCircle2,
    RefreshCw,
    ArrowRight,
    ArrowLeft,
    Undo2,
    ChevronDown,
    ChevronUp,
    CalendarClock,
    ReceiptText,
    UserX,
    Boxes,
    TreePalm,
    PiggyBank,
    Calculator,
    Eye,
    BookCheck,
} from 'lucide-react';
import * as YearEndService from '../../../../services/YearEndService';
import { resolveRoleAccount } from '../../../../services/api/chartRolesApi';
import { formatSek } from '../constants';

/**
 * The guided calculators — one card each in the "add" chooser. Field
 * flags drive the small form; param names match the backend's
 * AdjustmentService (amount / costAccountNumber / targetInventoryValue /
 * inventoryAccountNumber / description).
 */
const ADJUSTMENT_TYPES = [
    {
        type: 'periodisering_forutbetald',
        label: 'Förutbetald kostnad',
        tagline: 'Kostnad som hör till nästa år',
        help: 'T.ex. en försäkring eller hyra som betalats i år men gäller nästa år. '
            + 'Kostnaden flyttas till nästa år så att årets resultat blir rättvisande.',
        Icon: CalendarClock,
        fields: { amount: true, costAccount: true, description: true },
    },
    {
        type: 'periodisering_upplupen',
        label: 'Upplupen kostnad',
        tagline: 'Kostnad i år — fakturan kommer nästa år',
        help: 'T.ex. en revisionskostnad eller el för december där fakturan kommer efter '
            + 'årsskiftet. Kostnaden bokförs på rätt år redan nu.',
        Icon: ReceiptText,
        fields: { amount: true, costAccount: true, description: true },
    },
    {
        type: 'periodisering_upplupen_intakt',
        label: 'Upplupen intäkt',
        tagline: 'Intäkt i år — faktureras nästa år',
        help: 'T.ex. arbete utfört i december som faktureras i januari. Intäkten bokförs '
            + 'på rätt år redan nu, mot 1790.',
        Icon: CalendarClock,
        fields: { amount: true, revenueAccount: true, description: true },
    },
    {
        type: 'periodisering_forutbetald_intakt',
        label: 'Förutbetald intäkt',
        tagline: 'Fakturerat i år — hör till nästa år',
        help: 'T.ex. ett abonnemang fakturerat i december som gäller nästa år. Intäkten '
            + 'flyttas till nästa år via 2990 så att årets resultat blir rättvisande.',
        Icon: ReceiptText,
        fields: { amount: true, revenueAccount: true, description: true },
    },
    {
        type: 'osakra_kundfordringar',
        label: 'Osäkra kundfordringar',
        tagline: 'Kundfakturor som riskerar att inte betalas',
        help: 'Kundfakturor du inte tror kommer betalas. Den befarade förlusten bokförs '
            + 'som en kostnad i år.',
        Icon: UserX,
        fields: { amount: true, description: true },
    },
    {
        type: 'varulager',
        label: 'Lagervärdering',
        tagline: 'Justera lagret till inventerat värde',
        help: 'Värdera till det lägsta av inköpspris och verkligt värde. Ange värdet från '
            + 'din inventering, så bokförs skillnaden mot det bokförda lagervärdet.',
        Icon: Boxes,
        fields: { targetValue: true, inventoryAccount: true, description: true },
    },
    {
        type: 'slp_pension',
        label: 'Särskild löneskatt på pension',
        tagline: 'Stäm av löneskatten på pensionskostnader',
        help: 'Har företaget betalat tjänstepension ska särskild löneskatt (24,26 %) bokföras '
            + 'på årets pensionskostnader. Underlaget hämtas från kontona 7400–7499 och '
            + 'skillnaden mot redan bokförd löneskatt bokförs automatiskt.',
        Icon: PiggyBank,
        fields: { description: true },
    },
    {
        type: 'semesterloneskuld',
        label: 'Semesterlöneskuld',
        tagline: 'Intjänad men outtagen semester',
        help: 'Skulden för anställdas intjänade men outtagna semesterdagar. Beräknas '
            + 'automatiskt från lönekörningarna — inget belopp behöver anges.',
        Icon: TreePalm,
        fields: {},
    },
];

const TYPE_BY_KEY = ADJUSTMENT_TYPES.reduce((acc, t) => { acc[t.type] = t; return acc; }, {});

const EMPTY_FORM = {
    amount: '',
    costAccount: '',
    revenueAccount: '',
    targetValue: '',
    // Prefilled from the tenant's inventory_asset chart role when the form
    // opens — never a pinned number.
    inventoryAccount: '',
    description: '',
};

/** Parse a Swedish-typed amount ("12 500,50") into a number. */
function parseAmount(raw) {
    if (raw == null) return NaN;
    const cleaned = String(raw).replace(/[\s ]/g, '').replace(',', '.');
    if (cleaned === '') return NaN;
    return Number(cleaned);
}

const isFourDigitAccount = (value) => /^\d{4}$/.test(String(value || '').trim());

/**
 * Step 3 — Bokslutshändelser: guided year-end adjustments. Pick a
 * calculator card, fill the small form, "Förhandsgranska" fetches a
 * verifikat proposal from the backend (nothing booked), "Bokför" posts
 * it. Booked adjustments are listed with per-row undo (annulment via a
 * rättelseverifikation — nothing is ever deleted). No server gate on
 * completion: a company without adjustments completes the step freely.
 * Markup is the modern-card composition; shared ye-* chrome from
 * YearEndPage.
 */
const AdjustmentsStep = ({
    companyId,
    processId,
    completed,
    onComplete,
    onUndo,
    busy,
    gateError,
}) => {
    const [adjustments, setAdjustments] = useState(null); // null = not loaded yet
    const [listError, setListError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [candidates, setCandidates] = useState(null);

    const [activeType, setActiveType] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [proposal, setProposal] = useState(null);
    const [proposing, setProposing] = useState(false);
    const [booking, setBooking] = useState(false);
    const [formError, setFormError] = useState(null);

    const [expandedId, setExpandedId] = useState(null);
    const [undoingId, setUndoingId] = useState(null);

    // ---- Booked list ------------------------------------------------------

    const fetchList = useCallback(async ({ silent = false } = {}) => {
        if (!processId) return;
        if (!silent) setRefreshing(true);
        setListError(null);
        try {
            const resp = await YearEndService.listAdjustments(companyId, processId);
            setAdjustments(Array.isArray(resp?.data) ? resp.data : []);
        } catch (err) {
            setListError(err.response?.data?.message
                || 'Kunde inte hämta bokslutshändelserna. Försök igen senare.');
        } finally {
            setRefreshing(false);
        }
    }, [companyId, processId]);

    useEffect(() => { fetchList({ silent: true }); }, [fetchList]);

    // Periodiserings-kandidater: fakturor daterade efter balansdagen som
    // redan finns i systemet. Ren nudge — misslyckas hämtningen visas inget.
    useEffect(() => {
        if (!processId) return;
        let cancelled = false;
        YearEndService.getAdjustmentCandidates(companyId, processId)
            .then((resp) => { if (!cancelled) setCandidates(resp?.data || null); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [companyId, processId]);

    const list = adjustments || [];
    const activeCount = list.filter((a) => !Number(a.reversed)).length;

    // ---- Add-form ---------------------------------------------------------

    const typeDef = activeType ? TYPE_BY_KEY[activeType] : null;
    const isSemester = activeType === 'semesterloneskuld';

    const openType = (type) => {
        setActiveType(type);
        setForm(EMPTY_FORM);
        setProposal(null);
        setFormError(null);
        // Prefill the inventory account from the tenant's inventory_asset
        // chart role (1460 on BAS2024/BAS2026, 1400 on K1) — editable, and
        // the backend resolves its own default when left empty.
        if (TYPE_BY_KEY[type]?.fields?.inventoryAccount) {
            resolveRoleAccount(companyId, 'inventory_asset').then((leg) => {
                if (leg) {
                    setForm((prev) => (prev.inventoryAccount
                        ? prev
                        : { ...prev, inventoryAccount: leg.accountNumber }));
                }
            });
        }
    };

    const closeForm = () => {
        setActiveType(null);
        setProposal(null);
        setFormError(null);
    };

    const setField = (field) => (e) => {
        const { value } = e.target;
        setForm((prev) => ({ ...prev, [field]: value }));
        // Any edit invalidates a shown proposal — force a new preview.
        setProposal(null);
        setFormError(null);
    };

    const buildParams = () => {
        if (!typeDef) return {};
        const params = {};
        if (typeDef.fields.amount) params.amount = parseAmount(form.amount);
        if (typeDef.fields.costAccount) params.costAccountNumber = form.costAccount.trim();
        if (typeDef.fields.revenueAccount) params.revenueAccountNumber = form.revenueAccount.trim();
        if (typeDef.fields.targetValue) params.targetInventoryValue = parseAmount(form.targetValue);
        if (typeDef.fields.inventoryAccount) {
            params.inventoryAccountNumber = form.inventoryAccount.trim();
        }
        if (form.description.trim()) params.description = form.description.trim();
        return params;
    };

    const formValid = (() => {
        if (!typeDef) return false;
        if (typeDef.fields.amount) {
            const n = parseAmount(form.amount);
            if (!Number.isFinite(n) || n <= 0) return false;
        }
        if (typeDef.fields.costAccount && !isFourDigitAccount(form.costAccount)) return false;
        if (typeDef.fields.revenueAccount && !isFourDigitAccount(form.revenueAccount)) return false;
        if (typeDef.fields.targetValue) {
            const n = parseAmount(form.targetValue);
            if (!Number.isFinite(n) || n < 0) return false;
        }
        if (typeDef.fields.inventoryAccount
            && form.inventoryAccount.trim() !== ''
            && !isFourDigitAccount(form.inventoryAccount)) return false;
        return true;
    })();

    const handlePropose = async () => {
        setProposing(true);
        setFormError(null);
        try {
            const resp = await YearEndService.proposeAdjustment(
                companyId, processId, activeType, buildParams()
            );
            setProposal(resp?.data || null);
        } catch (err) {
            setFormError(err.response?.data?.message
                || 'Kunde inte ta fram förslaget. Försök igen.');
        } finally {
            setProposing(false);
        }
    };

    const handleBook = async () => {
        setBooking(true);
        setFormError(null);
        try {
            const resp = await YearEndService.bookAdjustment(
                companyId, processId, activeType, buildParams()
            );
            if (resp?.data?.booked === false) {
                toast(resp?.data?.message
                    || 'Ingen justering behövdes — saldot stämmer redan.');
            } else {
                toast.success('Bokslutshändelsen är bokförd som ett verifikat.');
            }
            closeForm();
            await fetchList({ silent: true });
        } catch (err) {
            setFormError(err.response?.data?.message
                || 'Kunde inte bokföra bokslutshändelsen.');
        } finally {
            setBooking(false);
        }
    };

    // ---- Undo (annulment) -------------------------------------------------

    const handleUndoAdjustment = async (adj) => {
        const label = TYPE_BY_KEY[adj.adjustment_type]?.label || adj.adjustment_type;
        const ok = window.confirm(
            `Vill du ångra ”${label}”?\n\n`
            + 'En rättelseverifikation skapas som nollställer bokningen — inget raderas '
            + 'ur bokföringen. Det är så rättelser ska göras enligt god redovisningssed.'
        );
        if (!ok) return;
        setUndoingId(adj.adjustment_id);
        try {
            await YearEndService.undoAdjustment(companyId, adj.adjustment_id);
            toast.success('Bokslutshändelsen är ångrad — en rättelseverifikation har skapats.');
            await fetchList({ silent: true });
        } catch (err) {
            toast.error(err.response?.data?.message
                || 'Kunde inte ångra bokslutshändelsen.');
        } finally {
            setUndoingId(null);
        }
    };

    // ---- Renderers --------------------------------------------------------

    const renderSemesterProposal = () => {
        const sa = proposal?.semesterAdjustment;
        if (!sa) return null;
        const noChange = Math.abs(Number(sa.adjustment?.total) || 0) < 0.005;
        const rows = [
            {
                konto: '2920 — Semesterlöneskuld',
                current: sa.currentBalance?.gross2920,
                target: sa.targetBalance?.gross2920,
                delta: sa.adjustment?.gross,
            },
            {
                konto: '2940 — Upplupna sociala avgifter',
                current: sa.currentBalance?.socSec2940,
                target: sa.targetBalance?.socSec2940,
                delta: sa.adjustment?.socSec,
            },
        ];
        return (
            <>
                <div className="modern-table-container" style={{ marginTop: '0.75rem' }}>
                    <Table responsive className="modern-table ye-mini-verifikat mb-0">
                        <thead>
                            <tr>
                                <th>Konto</th>
                                <th style={{ textAlign: 'right' }}>Bokfört saldo</th>
                                <th style={{ textAlign: 'right' }}>Beräknad skuld</th>
                                <th style={{ textAlign: 'right' }}>Justering</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.konto}>
                                    <td>{row.konto}</td>
                                    <td className="ye-num">{formatSek(row.current)}</td>
                                    <td className="ye-num">{formatSek(row.target)}</td>
                                    <td className="ye-num">{formatSek(row.delta)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
                {noChange && (
                    <div className="ye-well">
                        Skulden i bokföringen stämmer redan med beräkningen — ingen justering
                        behöver bokföras.
                    </div>
                )}
            </>
        );
    };

    const renderProposal = () => {
        if (!proposal) return null;
        const transactions = proposal.transactions || [];
        const semesterNoChange = isSemester
            && Math.abs(Number(proposal.semesterAdjustment?.adjustment?.total) || 0) < 0.005;

        return (
            <div className="ye-proposal">
                <div className="ye-proposal-head">
                    <span className="ye-badge ye-badge-primary">Förslag — inget är bokfört ännu</span>
                    <span className="ye-proposal-title">
                        <strong>{proposal.description}</strong>
                        {proposal.date ? ` · ${proposal.date}` : ''}
                    </span>
                </div>
                {proposal.explanation && <div className="ye-well">{proposal.explanation}</div>}

                {transactions.length > 0 && (
                    <div className="modern-table-container" style={{ marginTop: '0.75rem' }}>
                        <Table responsive className="modern-table ye-mini-verifikat mb-0">
                            <thead>
                                <tr>
                                    <th>Konto</th>
                                    <th>Beskrivning</th>
                                    <th style={{ textAlign: 'right' }}>Debet</th>
                                    <th style={{ textAlign: 'right' }}>Kredit</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.map((t, i) => (
                                    <tr key={i}>
                                        <td><strong>{t.accountNumber}</strong></td>
                                        <td>{t.description}</td>
                                        <td className="ye-num">
                                            {t.type === 'debit' ? formatSek(t.amount) : ''}
                                        </td>
                                        <td className="ye-num">
                                            {t.type === 'credit' ? formatSek(t.amount) : ''}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}

                {isSemester && renderSemesterProposal()}

                <div className="ye-proposal-actions">
                    <button
                        className="btn btn-primary d-inline-flex align-items-center gap-2"
                        onClick={handleBook}
                        disabled={booking || semesterNoChange}
                    >
                        <BookCheck size={16} />
                        {booking ? 'Bokför…' : 'Bokför'}
                    </button>
                    <button
                        className="btn btn-outline-secondary d-inline-flex align-items-center gap-2"
                        onClick={() => setProposal(null)}
                        disabled={booking}
                    >
                        Ändra uppgifterna
                    </button>
                </div>
            </div>
        );
    };

    const renderForm = () => {
        const { Icon } = typeDef;
        return (
            <div className="ye-adj-form">
                <button className="ye-back-link" onClick={closeForm} disabled={proposing || booking}>
                    <ArrowLeft size={14} />
                    Välj en annan typ
                </button>

                <div className="ye-adj-form-head">
                    <span className="ye-adj-card-icon"><Icon size={20} /></span>
                    <div>
                        <div className="ye-adj-card-label">{typeDef.label}</div>
                        <div className="ye-adj-help">{typeDef.help}</div>
                    </div>
                </div>

                <div className="ye-form-grid">
                    {typeDef.fields.amount && (
                        <div className="ye-field">
                            <label htmlFor="ye-adj-amount">Belopp (kr)</label>
                            <input
                                id="ye-adj-amount"
                                type="text"
                                inputMode="decimal"
                                placeholder="t.ex. 12 000"
                                value={form.amount}
                                onChange={setField('amount')}
                            />
                            <div className="ye-field-help">Belopp exklusive moms.</div>
                        </div>
                    )}
                    {typeDef.fields.costAccount && (
                        <div className="ye-field">
                            <label htmlFor="ye-adj-account">Kostnadskonto</label>
                            <input
                                id="ye-adj-account"
                                type="text"
                                inputMode="numeric"
                                maxLength={4}
                                placeholder="t.ex. 5010"
                                value={form.costAccount}
                                onChange={setField('costAccount')}
                            />
                            <div className="ye-field-help">
                                Kontot där kostnaden är bokförd (4 siffror).
                            </div>
                        </div>
                    )}
                    {typeDef.fields.revenueAccount && (
                        <div className="ye-field">
                            <label htmlFor="ye-adj-revenue-account">Intäktskonto</label>
                            <input
                                id="ye-adj-revenue-account"
                                type="text"
                                inputMode="numeric"
                                maxLength={4}
                                placeholder="t.ex. 3010"
                                value={form.revenueAccount}
                                onChange={setField('revenueAccount')}
                            />
                            <div className="ye-field-help">
                                Kontot där intäkten hör hemma (4 siffror).
                            </div>
                        </div>
                    )}
                    {typeDef.fields.targetValue && (
                        <div className="ye-field">
                            <label htmlFor="ye-adj-target">Inventerat lagervärde (kr)</label>
                            <input
                                id="ye-adj-target"
                                type="text"
                                inputMode="decimal"
                                placeholder="t.ex. 85 000"
                                value={form.targetValue}
                                onChange={setField('targetValue')}
                            />
                            <div className="ye-field-help">
                                Lagrets värde per balansdagen enligt din inventering.
                            </div>
                        </div>
                    )}
                    {typeDef.fields.inventoryAccount && (
                        <div className="ye-field">
                            <label htmlFor="ye-adj-invaccount">Lagerkonto</label>
                            <input
                                id="ye-adj-invaccount"
                                type="text"
                                inputMode="numeric"
                                maxLength={4}
                                value={form.inventoryAccount}
                                onChange={setField('inventoryAccount')}
                            />
                            <div className="ye-field-help">Standard är tenantens lagerkonto (rollen Lager).</div>
                        </div>
                    )}
                    {typeDef.fields.description && (
                        <div className="ye-field ye-field-wide">
                            <label htmlFor="ye-adj-desc">Beskrivning (frivillig)</label>
                            <input
                                id="ye-adj-desc"
                                type="text"
                                placeholder="t.ex. Företagsförsäkring jan–jun"
                                value={form.description}
                                onChange={setField('description')}
                            />
                            <div className="ye-field-help">Visas på verifikatet.</div>
                        </div>
                    )}
                </div>

                {isSemester && !proposal && (
                    <div className="ye-well">
                        Klicka på ”Beräkna” så räknar vi ut skulden från de bokförda
                        lönekörningarna och visar skillnaden mot det som redan är bokfört
                        på konto 2920 och 2940.
                    </div>
                )}

                {formError && (
                    <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                        {formError}
                    </div>
                )}

                {proposal ? renderProposal() : (
                    <div className="ye-proposal-actions">
                        <button
                            className="btn btn-primary d-inline-flex align-items-center gap-2"
                            onClick={handlePropose}
                            disabled={proposing || !formValid}
                        >
                            {isSemester ? <Calculator size={16} /> : <Eye size={16} />}
                            {proposing
                                ? (isSemester ? 'Beräknar…' : 'Hämtar förslag…')
                                : (isSemester ? 'Beräkna' : 'Förhandsgranska')}
                        </button>
                        <span className="ye-footer-hint">
                            Inget bokförs ännu — du får först se ett förslag.
                        </span>
                    </div>
                )}
            </div>
        );
    };

    const renderChooser = () => (
        <>
            <div className="ye-adj-grid">
                {ADJUSTMENT_TYPES.map(({ type, label, tagline, Icon }) => (
                    <button key={type} type="button" className="ye-adj-card" onClick={() => openType(type)}>
                        <span className="ye-adj-card-icon"><Icon size={20} /></span>
                        <span>
                            <span className="ye-adj-card-label">{label}</span>
                            <span className="ye-adj-card-tagline">{tagline}</span>
                        </span>
                    </button>
                ))}
            </div>
            <p className="text-muted mt-3 mb-0" style={{ fontSize: '0.8125rem', maxWidth: '68ch' }}>
                Två fall som hanteras med korten ovan: <strong>upplupen ränta</strong> på lån
                (ränta som avser i år men betalas nästa år) bokförs som en upplupen kostnad
                med räntekontot, t.ex. 8410. <strong>Pågående arbeten till fast pris</strong> som
                sträcker sig över årsskiftet har egna värderingsregler i K2 (kapitel 6) —
                stäm av med din redovisningskonsult och bokför via ett vanligt verifikat.
            </p>
        </>
    );

    const renderBookedList = () => {
        if (listError) {
            return <div className="alert alert-danger mb-0" style={{ fontSize: '0.875rem' }}>{listError}</div>;
        }
        if (adjustments === null) {
            return (
                <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                    Hämtar bokslutshändelserna…
                </p>
            );
        }
        if (list.length === 0) {
            return (
                <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                    Inga bokslutshändelser är bokförda ännu. Det är helt okej — alla företag
                    behöver inga.
                </p>
            );
        }
        return (
            <div className="modern-table-container">
                <Table responsive hover className="modern-table ye-adj-table mb-0">
                    <thead>
                        <tr>
                            <th>Typ</th>
                            <th>Verifikat</th>
                            <th>Bokförd</th>
                            <th style={{ textAlign: 'right' }}>Åtgärd</th>
                            <th style={{ width: 40 }} aria-label="Detaljer" />
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((adj) => {
                            const reversed = Boolean(Number(adj.reversed));
                            const expanded = expandedId === adj.adjustment_id;
                            const label = TYPE_BY_KEY[adj.adjustment_type]?.label || adj.adjustment_type;
                            const verifikat = adj.verification_number != null
                                ? `${adj.series || 'V'}${adj.verification_number}`
                                : '—';
                            return (
                                <React.Fragment key={adj.adjustment_id}>
                                    <tr className={reversed ? 'ye-adj-reversed' : ''}>
                                        <td>
                                            <span className="ye-adj-type-label">{label}</span>
                                            {reversed && (
                                                <span className="ye-badge ye-badge-neutral" style={{ marginLeft: 8 }}>
                                                    Ångrad
                                                </span>
                                            )}
                                        </td>
                                        <td><strong>{verifikat}</strong></td>
                                        <td>
                                            {adj.created_at
                                                ? moment(adj.created_at).format('YYYY-MM-DD')
                                                : '—'}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            {!reversed && (
                                                <button
                                                    className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                                                    onClick={() => handleUndoAdjustment(adj)}
                                                    disabled={completed || undoingId === adj.adjustment_id}
                                                >
                                                    <Undo2 size={13} />
                                                    {undoingId === adj.adjustment_id ? 'Ångrar…' : 'Ångra'}
                                                </button>
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button
                                                className="ye-icon-btn"
                                                onClick={() => setExpandedId(expanded ? null : adj.adjustment_id)}
                                                aria-label="Visa förklaring"
                                                title="Visa förklaring"
                                            >
                                                {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                            </button>
                                        </td>
                                    </tr>
                                    {expanded && (
                                        <tr className="ye-adj-detail-row">
                                            <td colSpan={5}>
                                                {adj.explanation && <div className="ye-well" style={{ marginTop: 0 }}>{adj.explanation}</div>}
                                                <div className="ye-field-help" style={{ marginTop: '0.4rem' }}>
                                                    {reversed
                                                        ? 'Den här händelsen är ångrad med en rättelseverifikation — båda verifikaten finns kvar i bokföringen.'
                                                        : 'Om du ångrar skapas en rättelseverifikation som nollställer bokningen — inget raderas ur bokföringen.'}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </Table>
            </div>
        );
    };

    // ---- Layout -----------------------------------------------------------

    return (
        <>
            <style>{`
                .ye-adj-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
                    gap: 0.75rem;
                }
                .ye-adj-card {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.75rem;
                    text-align: left;
                    background: #fff;
                    border: 1px solid #e5e7eb;
                    border-radius: 10px;
                    padding: 0.9rem 1rem;
                    cursor: pointer;
                    transition: border-color 0.15s ease, box-shadow 0.15s ease;
                }
                .ye-adj-card:hover, .ye-adj-card:focus-visible {
                    border-color: var(--primary);
                    box-shadow: 0 0 0 3px var(--rgba-primary-1);
                    outline: none;
                }
                .ye-adj-card-icon { color: var(--primary); flex-shrink: 0; margin-top: 2px; }
                .ye-adj-card-label {
                    display: block;
                    font-weight: 600;
                    font-size: 0.875rem;
                    color: var(--title, #374151);
                }
                .ye-adj-card-tagline {
                    display: block;
                    font-size: 0.78rem;
                    color: #6b7280;
                    margin-top: 2px;
                    line-height: 1.4;
                }
                .ye-adj-help {
                    font-size: 0.8125rem;
                    color: #6b7280;
                    margin-top: 0.25rem;
                    line-height: 1.5;
                    max-width: 62ch;
                }
                .ye-adj-form {
                    border: 1px solid #e5e7eb;
                    border-radius: 10px;
                    padding: 1rem 1.25rem;
                }
                .ye-adj-form-head {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.75rem;
                    margin: 0.75rem 0 1rem;
                }
                .ye-back-link {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.3rem;
                    background: none;
                    border: none;
                    padding: 0;
                    font-size: 0.8125rem;
                    font-weight: 600;
                    color: var(--primary);
                    cursor: pointer;
                }
                .ye-back-link:disabled { color: #9ca3af; cursor: default; }
                .ye-form-grid {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.75rem 1rem;
                }
                .ye-field { flex: 1 1 180px; max-width: 260px; }
                .ye-field-wide { flex: 2 1 320px; max-width: 480px; }
                .ye-field label {
                    display: block;
                    font-size: 0.78rem;
                    font-weight: 600;
                    color: #4b5563;
                    margin-bottom: 0.3rem;
                }
                .ye-field input {
                    width: 100%;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 0.45rem 0.75rem;
                    font-size: 0.875rem;
                    transition: border-color 0.2s ease, box-shadow 0.2s ease;
                }
                .ye-field input:focus {
                    outline: none;
                    border-color: var(--primary);
                    box-shadow: 0 0 0 3px var(--rgba-primary-1);
                }
                .ye-field-help { font-size: 0.75rem; color: #9ca3af; margin-top: 0.25rem; }
                .ye-proposal { margin-top: 1rem; }
                .ye-proposal-head {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    flex-wrap: wrap;
                }
                .ye-proposal-title { font-size: 0.875rem; color: var(--title, #374151); }
                .ye-proposal-actions {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    flex-wrap: wrap;
                    margin-top: 1rem;
                }
                .ye-mini-verifikat td.ye-num, .ye-adj-table td.ye-num {
                    text-align: right;
                    white-space: nowrap;
                    font-variant-numeric: tabular-nums;
                }
                .ye-section-heading {
                    font-size: 0.78rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                    color: #6b7280;
                    margin: 0 0 0.6rem;
                }
                .ye-adj-section + .ye-adj-section { margin-top: 1.75rem; }
                .ye-adj-reversed td { color: #9ca3af; }
                .ye-adj-reversed .ye-adj-type-label { text-decoration: line-through; }
                .ye-adj-detail-row td { background: #f9fafb; padding: 0.75rem 1rem; }
                .ye-icon-btn {
                    background: none;
                    border: none;
                    padding: 2px;
                    color: #6b7280;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                }
                .ye-icon-btn:hover { color: var(--primary); }
            `}</style>

            <div className="modern-card-header ye-step-header">
                <div>
                    <h4 className="section-title">Bokslutshändelser</h4>
                    <p className="section-subtitle">
                        Bokslutshändelser är justeringar som bara görs i samband med bokslutet —
                        till exempel att flytta en kostnad till rätt år (kallas periodisering)
                        eller att värdera lagret. Välj en händelse nedan, titta på förslaget och
                        bokför när det ser rätt ut. Har företaget inga sådana händelser går du
                        bara vidare.
                    </p>
                </div>
                <div className="action-buttons">
                    <span className="ye-badge ye-badge-primary">
                        {activeCount} bokförda
                    </span>
                    <button
                        className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
                        onClick={() => fetchList()}
                        disabled={refreshing}
                    >
                        <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
                        {refreshing ? 'Uppdaterar…' : 'Uppdatera listan'}
                    </button>
                </div>
            </div>

            <div className="modern-card-body">
                <div className="modern-card p-3 mb-3">
                    <Link to={`/${companyId}/periodisering`}>{candidates?.activePlanCount || 0} aktiva periodiseringsplaner sköts automatiskt</Link>
                    {(candidates?.belowThreshold || []).map(group=><div key={group.accountNumber} className="mt-3">
                        <strong>Konto {group.accountNumber}: {formatSek(group.totalAmount)}</strong><p>{group.wording}</p>
                        <p>{group.items.length} poster. Välj en periodiseringsplan, lägg till en bokslutsjustering nedan eller lämna beloppen oförändrade.</p>
                        <Link to={`/${companyId}/periodisering`}>Granska och skapa planer</Link>
                    </div>)}
                </div>
                {!completed && (candidates?.unpaidCashMethod?.supplierInvoices?.length > 0
                    || candidates?.unpaidCashMethod?.customerInvoices?.length > 0) && (
                    <div className="ye-well" style={{ marginTop: 0, marginBottom: '1.5rem' }}>
                        <strong>Kontantmetoden: obetalda fakturor ska bokföras per balansdagen</strong>
                        <div style={{ marginTop: '0.35rem' }}>
                            Med kontantmetoden bokförs fakturor först vid betalning — men vid
                            räkenskapsårets utgång ska alla då obetalda fordringar och skulder
                            bokföras (bokföringslagen 5 kap. 2 §). Bokför kundfakturorna som
                            kundfordran (1510) och leverantörsfakturorna som leverantörsskuld
                            (2440), inklusive momsen.
                        </div>
                        {candidates.unpaidCashMethod.customerInvoices?.length > 0 && (
                            <ul style={{ margin: '0.5rem 0 0' }}>
                                {candidates.unpaidCashMethod.customerInvoices.map((inv) => (
                                    <li key={`uc-${inv.invoice_number}-${inv.invoice_date}`}>
                                        {inv.invoice_date} — kundfaktura {inv.invoice_number || ''},{' '}
                                        {formatSek(inv.amount)} (obetald) <CustomerYearEndBooking companyId={companyId} invoice={inv} fiscalYearId={candidates.fiscalYearId} date={candidates.balansdag}/>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {candidates.unpaidCashMethod.supplierInvoices?.length > 0 && (
                            <ul style={{ margin: '0.5rem 0 0' }}>
                                {candidates.unpaidCashMethod.supplierInvoices.map((inv) => (
                                    <li key={`us-${inv.invoice_number}-${inv.invoice_date}`}>
                                        {inv.invoice_date} — {inv.supplier_name || 'Leverantör'}{' '}
                                        ({inv.invoice_number}), {formatSek(inv.amount)} (obetald){' '}
                                        <Link to={`/${companyId}/supplier-invoices/${inv.invoice_id}?yearEndFiscalYearId=${candidates.fiscalYearId}`}>Bokför vid bokslut</Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
                {!completed && (candidates?.supplierInvoices?.length > 0 || candidates?.customerInvoices?.length > 0) && (
                    <div className="ye-well" style={{ marginTop: 0, marginBottom: '1.5rem' }}>
                        <strong>Fakturor daterade efter årsskiftet — hör de till i år?</strong>
                        <div style={{ marginTop: '0.35rem' }}>
                            De här fakturorna är daterade efter balansdagen. Om kostnaden eller
                            intäkten i själva verket hör till det gamla året (t.ex. el eller
                            revision för december, fakturerad i januari) bokför du en{' '}
                            <em>upplupen kostnad</em> respektive <em>upplupen intäkt</em> nedan.
                            {/* Beloppsgränsen är versionsstyrd per processens regelverk
                                (K2 p. 2.4: 7 000 kr fr.o.m. BFNAR 2025:2 / FY2026+,
                                annars 5 000 kr) — servern avgör, klienten trycker. */}
                            {` Poster under ${{ 5000: '5 000', 7000: '7 000' }[candidates?.periodisering?.gransbelopp] || '5 000'} kr behöver inte periodiseras (K2).`}
                            {candidates?.periodisering?.vasentlighetsbegransning
                                ? ' Förenklingen får inte användas i den utsträckning den, ensam eller tillsammans med andra förenklingar, väsentligt påverkar företagets intäkter, kostnader eller ställning (K2 punkt 2.4B).'
                                : ''}
                        </div>
                        {candidates?.supplierInvoices?.length > 0 && (
                            <ul style={{ margin: '0.5rem 0 0' }}>
                                {candidates.supplierInvoices.map((inv) => (
                                    <li key={`s-${inv.invoice_number}-${inv.invoice_date}`}>
                                        {inv.invoice_date} — {inv.supplier_name || 'Leverantör'}{' '}
                                        ({inv.invoice_number}), {formatSek(inv.amount)}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {candidates?.customerInvoices?.length > 0 && (
                            <ul style={{ margin: '0.5rem 0 0' }}>
                                {candidates.customerInvoices.map((inv) => (
                                    <li key={`c-${inv.invoice_number}-${inv.invoice_date}`}>
                                        {inv.invoice_date} — kundfaktura {inv.invoice_number || ''},{' '}
                                        {formatSek(inv.amount)}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
                {completed ? (
                    <div className="ye-well" style={{ marginTop: 0, marginBottom: '1.5rem' }}>
                        Steget är markerat som klart. Ångra markeringen längst ner om du behöver
                        lägga till eller ångra en bokslutshändelse.
                    </div>
                ) : (
                    <div className="ye-adj-section">
                        <h5 className="ye-section-heading">Lägg till bokslutshändelse</h5>
                        {activeType ? renderForm() : renderChooser()}
                    </div>
                )}

                <div className="ye-adj-section">
                    <h5 className="ye-section-heading">Bokförda bokslutshändelser</h5>
                    {renderBookedList()}
                </div>

                {gateError && (
                    <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                        <strong>{gateError.message}</strong>
                    </div>
                )}
            </div>

            <div className="ye-step-footer">
                {completed ? (
                    <>
                        <span className="ye-footer-done">
                            <CheckCircle2 size={18} style={{ color: 'var(--bs-success)' }} />
                            Bokslutshändelserna är markerade som klara
                        </span>
                        <button
                            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                            onClick={onUndo}
                            disabled={busy}
                        >
                            <Undo2 size={14} />
                            Ångra markeringen
                        </button>
                    </>
                ) : (
                    <>
                        <span className="ye-footer-hint">
                            {activeCount > 0
                                ? `${activeCount} ${activeCount === 1 ? 'bokslutshändelse är bokförd' : 'bokslutshändelser är bokförda'} — markera steget som klart när du är färdig.`
                                : 'Inga bokslutshändelser att bokföra? Då kan du markera steget som klart direkt.'}
                        </span>
                        <button
                            className="btn btn-primary d-inline-flex align-items-center gap-2"
                            onClick={onComplete}
                            disabled={busy}
                        >
                            {busy ? 'Sparar…' : 'Markera som klart'}
                            <ArrowRight size={16} />
                        </button>
                    </>
                )}
            </div>
        </>
    );
};

export default AdjustmentsStep;
